import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSelector } from 'react-redux';
import { Box, Button, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { CreditApplication, CreditSubmission, Proposal, VendorOpportunity } from '@trnsact/trnsact-shared-types';
import { CREDIT_SUB_STAGE_OPTIONS } from 'modules/creditSubmission/constants';
import { getDecisionActiveStep } from 'modules/creditSubmission/lib';
import { GET_APPROVALS_BY_VENDOR_OPPORTUNITY_ID, UPDATE_PARSED_DECISION } from 'components/CompareApprovals/queries';
import { mapCreditSubmissionToApproval } from 'components/CompareApprovals/lib';
import { SelectField } from 'components/form/rhf/SelectField';
import { CALCULATE_FINANCE_PAYMENTS } from 'modules/creditSubmission/lenderDeskingPanel/api/calculateFinancePayments';
import { useCreditSubmission } from 'modules/creditSubmission/hooks/useCreditSubmission';
import { useProposal } from 'modules/proposals';
import { useNotifications } from 'modules/notification';
import { approvalNotesSchema } from '../../model';
import { useSubmissionDocuments } from '../../hooks/useSubmissionDocuments';
import { ApprovalFormValues, ApprovalNotesFormValues } from '../../types';
import {
  buildCalculatePaymentsVariables,
  buildParsedDecisionPayload,
  buildProposalCopyDto,
  normalizePaymentsByTerm,
  resolveLatestProposal,
} from '../../lib';
import { DECISIONS } from '../../constants';
import { DecisionSteps } from './DecisionSteps';
import { DeclinedIndicator } from './DeclinedIndicator';
import { DecisionChip } from './DecisionChip';
import { InfoBanner } from './InfoBanner';
import { ApprovalOverviewCard } from './ApprovalOverviewCard';
import { DeclineReasonsCard } from './DeclineReasonsCard';
import { SectionCard } from './SectionCard';
import { DecisionLetterTable } from './DecisionLetterTable';
import { SubmissionDocumentsTable } from './SubmissionDocumentsTable';
import { EditApprovalOverviewDialog } from '../dialogs';
import LenderSubmissionFileUpLoadModal from 'pages/VendorOpportunity/LenderSubmissionFileUploadModal';
import SpecificTasks from 'pages/VendorOpportunity/SpecificTasks';

const UntypedSpecificTasks = SpecificTasks as React.ComponentType<any>;

interface Props {
  creditSubmission: CreditSubmission & {
    proposals?: Proposal[];
    contingencies: string[];
    creditSubmissionDeclineReasonId?: string | null;
    internalNotes?: string | null;
  };
  creditApp: CreditApplication;
  vo: VendorOpportunity;
  onBack: () => void;
}

export const SubmissionDetailsView = ({ creditSubmission, creditApp, vo, onBack }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dynamicsCreditSubmissionId = creditSubmission.creditSubId!;
  const [uploadModalType, setUploadModalType] = useState<'decisionLetter' | 'submissionDocs' | null>(null);

  const vp = useSelector((state: any) => state.vp);
  const account = useSelector((state: any) => state.account);
  const userProfile = useSelector((state: any) => state.userProfile);

  const {
    data: approvalsData,
    loading: isApprovalsLoading,
    refetch: refetchApprovals,
  } = useQuery(GET_APPROVALS_BY_VENDOR_OPPORTUNITY_ID, {
    variables: { voId: vo.dynamicsVendorOpportunityId },
    skip: !vo.dynamicsVendorOpportunityId,
    fetchPolicy: 'cache-and-network',
  });

  const { approval, currentDecision, declineReasons, proposal } = useMemo(() => {
    const submissions = approvalsData?.creditSubmissionsByDynamicsVOId ?? [];
    const match = submissions.find((cs: any) => cs?.creditSubmissionId === creditSubmission.creditSubmissionId);
    const parsedData = match?.creditDecisionParsedData?.parsedData;
    return {
      approval: match ? mapCreditSubmissionToApproval(match) : null,
      currentDecision: match?.decision ?? creditSubmission.decision,
      declineReasons: (parsedData?.declineReasons ?? []).filter(Boolean) as string[],
      proposal: resolveLatestProposal(match?.proposals),
    };
  }, [approvalsData, creditSubmission.creditSubmissionId, creditSubmission.decision]);

  const activeStep = getDecisionActiveStep(currentDecision);

  const {
    isLoading: isDocumentsLoading,
    decisionLetters,
    csDocuments,
    toggleLenderAccess,
    refetch,
  } = useSubmissionDocuments({
    creditSubId: dynamicsCreditSubmissionId,
    voId: vo.vendorOpportunityId!,
    creditSubmissionId: creditSubmission.creditSubmissionId ?? undefined,
  });
  const lastSavedRef = useRef<ApprovalNotesFormValues>({
    transactionSummary: creditSubmission.transactionSummary ?? '',
    lenderNotes: creditSubmission.notes ?? '',
    internalNotes: creditSubmission.internalNotes ?? '',
    stage: creditSubmission.creditSubStage ?? null,
  });

  const { handleUpdateCreditSubmission } = useCreditSubmission();
  const { showNotification } = useNotifications();

  const [updateParsedDecision] = useMutation(UPDATE_PARSED_DECISION, {
    refetchQueries: ['GetApprovalsByVendorOpportunityId'],
  });
  const { handleGetProposalSnapshot, handleCreateProposal } = useProposal();
  const [calculatePayments] = useLazyQuery(CALCULATE_FINANCE_PAYMENTS);

  const { control, reset } = useForm<ApprovalNotesFormValues>({
    resolver: zodResolver(approvalNotesSchema),
    defaultValues: {
      transactionSummary: creditSubmission.transactionSummary ?? '',
      lenderNotes: creditSubmission.notes ?? '',
      internalNotes: creditSubmission.internalNotes ?? '',
      stage: creditSubmission.creditSubStage ?? null,
    },
  });

  const stage = useWatch({ control, name: 'stage' });

  useEffect(() => {
    containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    const nextValues: ApprovalNotesFormValues = {
      transactionSummary: creditSubmission.transactionSummary ?? '',
      lenderNotes: creditSubmission.notes ?? '',
      internalNotes: creditSubmission.internalNotes ?? '',
      stage: creditSubmission.creditSubStage ?? null,
    };
    reset(nextValues);
    lastSavedRef.current = nextValues;
  }, [creditSubmission, reset]);

  const saveFieldOnBlur = async (
    fieldName: 'transactionSummary' | 'lenderNotes' | 'internalNotes' | 'stage',
    value: string | number | null | undefined,
    successMessage: string
  ) => {
    const savedValue = lastSavedRef.current?.[fieldName];
    if (savedValue === value) return;

    let payload: Record<string, any>;

    if (fieldName === 'transactionSummary') {
      payload = { transactionSummary: value };
    } else if (fieldName === 'lenderNotes') {
      payload = { notes: value };
    } else if (fieldName === 'internalNotes') {
      payload = { internalNotes: value };
    } else {
      payload = { stage: value };
    }

    try {
      await handleUpdateCreditSubmission(dynamicsCreditSubmissionId, payload);
      lastSavedRef.current = { ...lastSavedRef.current, [fieldName]: value as any };
      showNotification(successMessage);
    } catch {
      showNotification('Something went wrong while saving', { type: 'error' });
    }
  };

  const handleSaveOverview = useCallback(
    async (approvalValues: ApprovalFormValues, conditions: string[]) => {
      const parsedDecisionId = approval?.parsedDecisionId;

      try {
        if (parsedDecisionId) {
          await updateParsedDecision({
            variables: {
              input: { parsedDecisionId, parsedData: buildParsedDecisionPayload(approvalValues, conditions) },
            },
          });
        } else {
          await handleUpdateCreditSubmission(dynamicsCreditSubmissionId, {
            approvedAmount: approvalValues.approvalAmount,
            approvedRate: approvalValues.interestRate ? Number(approvalValues.interestRate) : null,
            decision: approvalValues.decision,
            contingencies: conditions.filter(c => c.trim() !== ''),
          });

          const proposalId = proposal?.proposalId;
          if (proposalId) {
            const snapshot = (await handleGetProposalSnapshot(proposalId)) as Record<string, any> | null;
            if (snapshot) {
              const calcResult = await calculatePayments({
                variables: buildCalculatePaymentsVariables(snapshot, approvalValues),
              });
              const pricing = normalizePaymentsByTerm(
                calcResult.data?.calculateMultiplePayments?.[0]?.paymentOptions ?? []
              );
              await handleCreateProposal(
                buildProposalCopyDto(
                  snapshot,
                  approvalValues,
                  pricing,
                  vo.vendorOpportunityId!,
                  creditSubmission.creditSubmissionId ?? undefined
                )
              );
            }
          }
        }

        await refetchApprovals();

        showNotification('Changes saved');
      } catch {
        showNotification('Something went wrong while saving', { type: 'error' });
      }
    },
    [
      approval?.parsedDecisionId,
      creditSubmission,
      dynamicsCreditSubmissionId,
      handleUpdateCreditSubmission,
      updateParsedDecision,
      handleGetProposalSnapshot,
      handleCreateProposal,
      calculatePayments,
      vo.vendorOpportunityId,
      refetchApprovals,
      showNotification,
    ]
  );

  return (
    <Box
      ref={containerRef}
      sx={{ display: 'flex', flexDirection: 'column', gap: 4, p: 3, bgcolor: 'background.paper' }}
    >
      <Stack gap={2}>
        <Box>
          <Button size="small" startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ textTransform: 'none' }}>
            Back to Submissions list
          </Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" component="span">
            {creditApp.businessName}
          </Typography>
          <DecisionChip decision={currentDecision} />
          {creditSubmission.submissionDate && (
            <Typography variant="body1" color="text.secondary" sx={{ ml: 'auto' }}>
              Submission Date: {creditSubmission.submissionDate}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Typography variant="subtitle2" component="span">
              Decision
            </Typography>
            {currentDecision === DECISIONS.DECLINED ? (
              <DeclinedIndicator />
            ) : (
              <DecisionSteps
                activeStep={activeStep}
                lastStepLabel={currentDecision === DECISIONS.PRE_APPROVED ? DECISIONS.PRE_APPROVED : undefined}
              />
            )}
          </Box>

          <Box>
            <SelectField
              name="stage"
              label="Stage"
              control={control}
              options={CREDIT_SUB_STAGE_OPTIONS}
              variant="outlined"
              sx={{ width: 300 }}
              inputProps={{
                size: 'small',
                variant: 'outlined',
                onBlur: () => saveFieldOnBlur('stage', stage, 'Stage saved'),
              }}
            />
          </Box>
        </Box>

        <InfoBanner
          message={
            currentDecision === DECISIONS.AWAITING_REVIEW || !currentDecision
              ? 'Awaiting lender decision. Approval terms and financing options will appear once a response is received.'
              : currentDecision === DECISIONS.REQUESTED_ADDITIONAL_INFO
                ? 'Additional information is required before the lender can continue reviewing this submission.'
                : currentDecision === DECISIONS.DECLINED
                  ? 'This submission was declined by the lender. Decline reasons and related documents are available below.'
                  : currentDecision === DECISIONS.PRE_APPROVED
                    ? 'Lender has issued a preliminary approval for this submission. Final approval terms will be confirmed once the review is complete.'
                    : 'Approval terms, financing options, and lender conditions are available in the Approval Overview below.'
          }
        />
      </Stack>

      {currentDecision === DECISIONS.DECLINED && (
        <DeclineReasonsCard parsedReasons={declineReasons} creditSubmission={creditSubmission} />
      )}

      <ApprovalOverviewCard
        vo={vo}
        approval={approval}
        isLoading={isApprovalsLoading}
        creditSubmission={creditSubmission}
        proposal={proposal}
        formControl={control}
        decisionStatus={currentDecision}
        onSaveField={saveFieldOnBlur}
        onSaveOverview={handleSaveOverview}
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'stretch' }}>
        <Stack gap={2} sx={{ flex: '1 1 400px', minWidth: 0 }}>
          <SectionCard
            title="Decision Letter"
            actionBtn={
              <Button
                size="small"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={() => setUploadModalType('decisionLetter')}
              >
                Upload document
              </Button>
            }
            emptyMessage={
              !isDocumentsLoading && !decisionLetters.length ? 'No Decision Letter uploaded yet' : undefined
            }
          >
            {(isDocumentsLoading || decisionLetters.length > 0) && (
              <DecisionLetterTable decisionLetters={decisionLetters} isLoading={isDocumentsLoading} />
            )}
          </SectionCard>
          <SectionCard
            title="Credit Submission Documents"
            actionBtn={
              <Button
                size="small"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={() => setUploadModalType('submissionDocs')}
              >
                Upload document
              </Button>
            }
            emptyMessage={
              !isDocumentsLoading && !csDocuments.length ? 'No Credit Submission Documents uploaded yet' : undefined
            }
          >
            {(isDocumentsLoading || csDocuments.length > 0) && (
              <SubmissionDocumentsTable
                documents={csDocuments}
                isLoading={isDocumentsLoading}
                onToggleLenderAccess={toggleLenderAccess}
              />
            )}
          </SectionCard>
        </Stack>

        {/* <SectionCard title="Tasks" sx={{ flex: 1 }}> 
            </SectionCard> */}
        {vp.showTasks === 'show' && (
          <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
            <UntypedSpecificTasks
              vo={vo}
              account={account}
              userProfile={userProfile}
              creditSubId={dynamicsCreditSubmissionId}
              lenderContact={(creditSubmission as any).lenderContactInfo}
              submissionsTable={true}
              onTasksLoaded={() => {}}
            />
          </Box>
        )}
      </Box>

      <LenderSubmissionFileUpLoadModal
        open={uploadModalType !== null}
        onClose={() => setUploadModalType(null)}
        refetchDocs={refetch}
        voId={vo.vendorOpportunityId}
        vo={vo}
        lenderContactInfo={(creditSubmission as any).lenderContactInfo}
        relatedTo="submission"
        creditSubmissionId={dynamicsCreditSubmissionId}
      />

      <EditApprovalOverviewDialog />
    </Box>
  );
};
